import { Controller } from "@hotwired/stimulus"
import { debounce } from "lodash"

export default class extends Controller {
  static targets = [
    "search",
    "searchItem",
    "territoryForm",
    "locationResults",
    "hiddenTerritoryId",
    "hiddenTerritoryName",
    "sortRadio",
    "hiddenSorter",
    "searchForm"
  ]

  connect() {
    this.selectedIndex = 0;
    this.lastSearchQuery = '';
    this.isKeyboardNavigation = false;
    this.initialHighlightDone = false;
    this.hasInitialResults = false;

    this.initializeExistingValues();

    this.searchTarget.addEventListener("input", this.handleSearchInput.bind(this));
    this.searchTarget.addEventListener("focus", this.handleSearchInput.bind(this));
    this.searchTarget.addEventListener("click", this.handleSearchClick.bind(this));
    document.addEventListener("keydown", this.handleGlobalKeyDown.bind(this));
    document.addEventListener("click", this.handleClickOutside.bind(this));

    if (this.searchTarget.value.trim().length === 0) {
      this.hasInitialResults = true;
    }

    this.setupListItemListeners();

    const currentSort = this.hiddenSorterTarget?.value || 'date';
    if (this.hiddenSorterTarget) {
      this.hiddenSorterTarget.value = currentSort;
    }

    if (this.sortRadioTargets) {
      this.sortRadioTargets.forEach(radio => {
        if (radio.value.toLowerCase() === currentSort.toLowerCase()) {
          radio.checked = true;
          this.updateRadioStyles(radio);
        }
      });
    }

    this.setupMutationObserver();
  }

  initializeExistingValues() {
    const territoryId = this.hiddenTerritoryIdTarget.value;
    const territoryName = this.hiddenTerritoryNameTarget.value;

    if (territoryId && territoryName) {
      this.searchTarget.value = territoryName;
    }
  }

  handleSearchInput = debounce((event) => {
    const query = event.target.value.trim();
    this.locationResultsTarget.classList.remove("hidden");
    this.lastSearchQuery = query;
    this.fetchTerritories(query);
    this.hasInitialResults = true;
  }, 200);

  fetchTerritories(query) {
    fetch(`/territories?territory=${encodeURIComponent(query)}`, {
      method: 'GET',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => response.text())
      .then(html => {
        Turbo.renderStreamMessage(html);

        setTimeout(() => {
          this.setupListItemListeners();
          const items = this.locationResultsTarget.querySelectorAll('li');
          if (items.length > 0) {
            this.selectedIndex = 0;
            this.isKeyboardNavigation = true;
            this.updateHighlight(items);
          }
        }, 50);
      })
      .catch(error => {
        console.error('Error fetching territories:', error);
      });
  }

  selectLocation(event) {
    const item = event.currentTarget;
    const locationName = item.dataset.name;
    this.searchTarget.value = locationName;
    this.locationResultsTarget.classList.add("hidden");
    this.hiddenTerritoryIdTarget.value = item.dataset.id;
    this.hiddenTerritoryNameTarget.value = locationName;
    this.selectedIndex = -1;
    this.initialHighlightDone = false;
  }

  selectLocationByIndex(item) {
    if (!item) return;

    const locationName = item.dataset.name;
    this.searchTarget.value = locationName;
    this.locationResultsTarget.classList.add("hidden");
    this.hiddenTerritoryIdTarget.value = item.dataset.id;
    this.hiddenTerritoryNameTarget.value = locationName;
    this.selectedIndex = -1;
    this.initialHighlightDone = false;
  }

  setupListItemListeners() {
    const items = this.locationResultsTarget.querySelectorAll('li');
    items.forEach((item, index) => {
      if (!item.hasListeners) {
        item.hasListeners = true;

        item.addEventListener('mouseenter', () => {
          if (this.isKeyboardNavigation) {
            this.isKeyboardNavigation = false;
          }
          this.selectedIndex = index;
          this.updateHighlight(items);
        });

        item.addEventListener('mouseleave', () => {
          if (!this.isKeyboardNavigation) {
            item.classList.remove('bg-gray-100');
            const divisionTypeContainer = item.querySelector('.division-type-text');
            if (divisionTypeContainer) {
              divisionTypeContainer.classList.remove('text-black');
            }
          }
        });
      }
    });
  }

  updateHighlight(items) {
    items.forEach((item, index) => {
      const divisionTypeContainer = item.querySelector('.division-type-text');
      const isSelected = index === this.selectedIndex;

      if (this.isKeyboardNavigation && isSelected) {
        requestAnimationFrame(() => {
          item.classList.remove('hover:bg-gray-100');
          item.classList.add('bg-gray-100');
          if (divisionTypeContainer) {
            divisionTypeContainer.classList.add('text-black');
          }

          item.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth'
          });
        });
      } else {
        requestAnimationFrame(() => {
          if (!this.isKeyboardNavigation) {
            item.classList.add('hover:bg-gray-100');
          } else {
            item.classList.remove('hover:bg-gray-100');
          }

          if (!isSelected || this.isKeyboardNavigation) {
            item.classList.remove('bg-gray-100');
            if (divisionTypeContainer) {
              divisionTypeContainer.classList.remove('text-black');
            }
          }
        });
      }
    });
  }

  handleGlobalKeyDown(event) {
    const results = this.locationResultsTarget;
    const items = results.querySelectorAll('li');

    if (results.classList.contains('hidden') || items.length === 0) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.submitSearch(event);
      }
      return;
    }

    switch(event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.isKeyboardNavigation = true;

        requestAnimationFrame(() => {
          if (this.selectedIndex < 0) {
            this.selectedIndex = event.key === 'ArrowDown' ? 0 : items.length - 1;
          } else {
            if (event.key === 'ArrowDown') {
              this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
            } else {
              this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
            }
          }

          this.updateHighlight(items);
        });
        break;

      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex === -1 && items.length > 0) {
          this.selectedIndex = 0;
        }
        if (this.selectedIndex >= 0) {
          this.selectLocationByIndex(items[this.selectedIndex]);
          this.submitSearch(event);
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.locationResultsTarget.classList.add('hidden');
        this.selectedIndex = -1;
        this.isKeyboardNavigation = false;
        this.initialHighlightDone = false;
        this.updateHighlight(items);
        break;
    }
  }

  setupMutationObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          if (!this.initialHighlightDone) {
            this.initialHighlightDone = true;
            requestAnimationFrame(() => {
              const items = this.locationResultsTarget.querySelectorAll('li');
              if (items.length > 0) {
                this.selectedIndex = 0;
                this.isKeyboardNavigation = true;
                this.setupListItemListeners();
                this.updateHighlight(items);
              }
            });
          }
        }
      });
    });

    this.observer.observe(this.locationResultsTarget, {
      childList: true,
      subtree: true
    });
  }

  locationResultsTargetConnected() {
    const items = this.locationResultsTarget.querySelectorAll('li');
    if (items.length > 0) {
      this.selectedIndex = 0;
      this.setupListItemListeners();
      this.updateHighlight(items);
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleGlobalKeyDown.bind(this));
    document.removeEventListener("click", this.handleClickOutside.bind(this));
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  handleClickOutside = (event) => {
    if (!this.searchTarget.contains(event.target) &&
      !this.locationResultsTarget.contains(event.target)) {
      this.locationResultsTarget.classList.add("hidden");
      this.initialHighlightDone = false;
    }
  }

  handleSearchClick(event) {
    event.stopPropagation();
    const query = this.searchTarget.value.trim();

    if (query.length > 2) {

      if (!this.hasInitialResults || this.lastSearchQuery === '') {
        this.lastSearchQuery = query;
        this.territoryFormTarget.requestSubmit();
        this.hasInitialResults = true;
        return;
      }
      this.locationResultsTarget.classList.remove("hidden");
      this.initialHighlightDone = false;
      const items = this.locationResultsTarget.querySelectorAll('li');
      if (items.length > 0) {
        this.selectedIndex = 0;
        this.isKeyboardNavigation = true;
        this.updateHighlight(items);
      }
    }
  }


  clearTerritoryFields() {
    this.hiddenTerritoryIdTarget.value = '';
    this.hiddenTerritoryNameTarget.value = '';
  }


  handleSort(event) {
    const selectedRadio = event.target;
    this.updateRadioStyles(selectedRadio);
    this.hiddenSorterTarget.value = selectedRadio.value.toLowerCase();
    this.searchFormTarget.requestSubmit();
  }

  updateRadioStyles(selectedRadio) {
    this.sortRadioTargets.forEach(radio => {
      const label = radio.closest('label');
      if (radio === selectedRadio) {
        label.classList.remove('bg-white', 'text-gray-900', 'ring-1', 'ring-gray-300', 'ring-inset', 'hover:bg-gray-50', 'cursor-pointer');
        label.classList.add('bg-gray-600', 'text-white', 'cursor-default');
      } else {
        label.classList.remove('bg-gray-600', 'text-white', 'cursor-default');
        label.classList.add('bg-white', 'text-gray-900', 'ring-1', 'ring-gray-300', 'ring-inset', 'hover:bg-gray-50', 'cursor-pointer');
      }
    });
  }

  submitSearch(event) {
    event.preventDefault();
    this.locationResultsTarget.classList.add("hidden");
    this.searchFormTarget.requestSubmit();
  }
}
